import React, { useState, useEffect } from 'react';
import Grid from "@mui/material/Grid2";
import Table from "@mui/material/Table";
import Chip from "@mui/material/Chip";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import { TextField, MenuItem, Typography } from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from "@mui/material/LinearProgress";
import TablePagination from "@mui/material/TablePagination";
import { MESSAGE_ERRORS } from "../../common/constants/scheduleModule/messagesConstants.js";
import { SIZES, VARIANTS, ALIGN, HTML_TAGS } from "../../common/constants/commonConstants.js";
import { UPCOMING_MESSAGE_TABLE_COLUMNS, UPCOMING_MESSAGE_TABLE_COLUMN_KEYS } from "../../common/constants/scheduleModule/messagesConstants.js";
import { getupcomingMessages } from "../../services/scheduleModule/scheduledataServices.js";
import { useClientTimeZone, formatDateInZone, zoneAbbr } from "../../utils/clienttimezone";
import RefreshCountdonwn from "../campaignModule/refreshCountdonwn.jsx";

const REFRESH_INTERVAL = 30;

const getStatusChip = (sent) => (
    <Chip
        label={sent ? "Sent" : "Pending"}
        size="small"
        sx={{
            backgroundColor: sent ? "#e8f5e9" : "#fff3e0",
            color: sent ? "#2e7d32" : "#e65100",
            fontWeight: 600,
            fontSize: "0.75rem",
        }}
    />
);

const UpcomingMessages = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [messageLogs, setMessageLogs] = useState([]);
    const [filteredMessagesLength, setFilteredMessagesLength] = useState(0);
    const [loadingMessages, setLoadingMessages] = useState(true);
    const [monthFilter, setMonthFilter] = useState("today");
    const [statusFilter, setStatusFilter] = useState("sent");
    const [searchMessage, setSearchMessage] = useState("");
    const [resetSignal, setResetSignal] = useState(0);

    // Send times render in the configured client zone, not the browser's.
    const { timeZone } = useClientTimeZone();

    const fetchMessages = async (pageNumber = page, rows = rowsPerPage, search = searchMessage, showLoader = true) => {
        try {
            if (showLoader) setLoadingMessages(true);
            const response = await getupcomingMessages(monthFilter, pageNumber, rows, search, statusFilter);
            setMessageLogs(response.data);
            setFilteredMessagesLength(response.totalCount);
        } catch (error) {
            console.error(MESSAGE_ERRORS.FETCH_MESSAGES_ERROR, error);
        } finally {
            if (showLoader) setLoadingMessages(false);
        }
    };

    useEffect(() => {
        setPage(0);
        fetchMessages(0, rowsPerPage, searchMessage, true);
    }, [monthFilter, statusFilter, searchMessage]);

    // 5-second silent refresh — same pattern as Processes.
    useEffect(() => {
        const intervalId = setInterval(() => {
            fetchMessages(page, rowsPerPage, searchMessage, false);
            setResetSignal(prev => prev + 1);
        }, REFRESH_INTERVAL * 1000);
        return () => clearInterval(intervalId);
    }, [monthFilter, statusFilter, searchMessage, page, rowsPerPage]);

    const tzLabel = zoneAbbr(timeZone);

    return (
        <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={SIZES.GROW} sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        value={searchMessage}
                        variant={VARIANTS.OUTLINED}
                        label="Search Messages"
                        onChange={(e) => setSearchMessage(e.target.value)}
                    />
                    <TextField
                        select
                        size={SIZES.SMALL}
                        label="Filter"
                        value={monthFilter}
                        onChange={(e) => setMonthFilter(e.target.value)}
                        sx={{ ml: 2, width: 250 }}
                    >
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="current">Current Month</MenuItem>
                        <MenuItem value="last2">Last 2 Months</MenuItem>
                        <MenuItem value="last3">Last 3 Months</MenuItem>
                    </TextField>
                    <TextField
                        select
                        size={SIZES.SMALL}
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ ml: 2, width: 150 }}
                    >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="sent">Sent</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                    </TextField>
                </Grid>
            </Grid>
            <RefreshCountdonwn refreshInterval={REFRESH_INTERVAL} resetSignal={resetSignal} />

            {/* The Message Date column is the send time in the tenant's zone.
                The Timezone column stays as-is — that's the customer's own
                zone from the ingest file, a different thing entirely. */}
            <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                Message dates shown in {tzLabel}
            </Typography>

            <TableContainer sx={{ mt: 2 }}>
                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {UPCOMING_MESSAGE_TABLE_COLUMNS.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sx={{
                                        width:
                                            column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.MESSAGE_DATE ? 200 :
                                            column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.CUSTOMER_ID ? 100 :
                                            column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.MESSAGE ? 500 :
                                            column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.CHANNEL ? 100 :
                                            column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.TIMEZONE ? 125 :
                                            column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.MESSAGE_SENT ? 110 :
                                            column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.CAMPAIGN ? 125 :
                                            column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.SCHEDULE ? 150 : 100,
                                    }}
                                >
                                    {column.id === UPCOMING_MESSAGE_TABLE_COLUMN_KEYS.MESSAGE_DATE
                                        ? `${column.label} (${tzLabel})`
                                        : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingMessages ? (
                            <TableRow>
                                <TableCell colSpan={UPCOMING_MESSAGE_TABLE_COLUMNS.length} align={ALIGN.CENTER}>
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : messageLogs.length > 0 ? (
                            messageLogs.map((message) => (
                                <TableRow hover tabIndex={-1} key={message.Id}>
                                    <TableCell>{formatDateInZone(message.msg_scheduled_at, timeZone)}</TableCell>
                                    <TableCell>{message.customerid}</TableCell>
                                    <TableCell>{message.campaignSchedulemsg}</TableCell>
                                    <TableCell>{message.channeltype}</TableCell>
                                    <TableCell>{message.timezone ?? "-"}</TableCell>
                                    <TableCell>{getStatusChip(!!message.msg_sent)}</TableCell>
                                    <TableCell>{message.campaignname}</TableCell>
                                    <TableCell>{message.CampaignScheduleName}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={UPCOMING_MESSAGE_TABLE_COLUMNS.length} align={ALIGN.CENTER}>
                                    No records found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={filteredMessagesLength}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => {
                        setPage(newPage);
                        fetchMessages(newPage, rowsPerPage, searchMessage);
                    }}
                    onRowsPerPageChange={(event) => {
                        const rows = parseInt(event.target.value, 10);
                        setRowsPerPage(rows);
                        setPage(0);
                        fetchMessages(0, rows, searchMessage);
                    }}
                />
            </TableContainer>
        </>
    );
};

export default UpcomingMessages;